import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { AlertCircle, CheckCircle, Info, AlertTriangle, X } from 'lucide-react';

export const AlertManager = ({ alerts, onResolveAlert }) => {
  const [visibleAlerts, setVisibleAlerts] = useState([]);

  useEffect(() => {
    // Filter alerts to show only recent unresolved ones
    const recentAlerts = alerts?.filter(alert => 
      !alert.resolved && 
      new Date(alert.created_at) > Date.now() - 24 * 60 * 60 * 1000 // Last 24 hours
    ) || [];
    
    setVisibleAlerts(recentAlerts);
  }, [alerts]);

  const getAlertIcon = (type) => {
    switch (type) {
      case 'error':
      case 'critical':
        return <AlertCircle className="w-5 h-5" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5" />;
      case 'info':
        return <Info className="w-5 h-5" />;
      default:
        return <AlertCircle className="w-5 h-5" />;
    }
  };

  const getAlertColor = (type) => {
    switch (type) {
      case 'error':
      case 'critical':
        return 'border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/10 dark:text-red-200';
      case 'warning':
        return 'border-yellow-200 bg-yellow-50 text-yellow-800 dark:border-yellow-800 dark:bg-yellow-900/10 dark:text-yellow-200';
      case 'info':
        return 'border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-900/10 dark:text-blue-200';
      default:
        return 'border-gray-200 bg-gray-50 text-gray-800 dark:border-gray-600 dark:bg-gray-900/10 dark:text-gray-200';
    }
  };

  const handleResolveAlert = async (alertId) => {
    try {
      await onResolveAlert(alertId);
      setVisibleAlerts(prev => prev.filter(alert => alert.id !== alertId));
      toast.success('Alert resolved');
    } catch (error) {
      toast.error('Failed to resolve alert');
    }
  };

  if (visibleAlerts.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2 mb-6">
      {visibleAlerts.map((alert) => (
        <div 
          key={alert.id} 
          className={`border rounded-lg p-4 ${getAlertColor(alert.type)}`}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              {getAlertIcon(alert.type)}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-medium">{alert.component.charAt(0).toUpperCase() + alert.component.slice(1)} Alert</h4>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-white/50 dark:bg-gray-900/50">
                    {alert.type.toUpperCase()}
                  </span>
                </div>
                <p className="text-sm">{alert.message}</p>
                {alert.details && Object.keys(alert.details).length > 0 && (
                  <div className="mt-2 text-xs space-y-1">
                    {Object.entries(alert.details).map(([key, value]) => (
                      <div key={key} className="flex gap-2">
                        <span className="font-medium">{key}:</span>
                        <span>{typeof value === 'object' ? JSON.stringify(value) : value}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="text-xs mt-2 opacity-75">
                  {new Date(alert.created_at).toLocaleString()}
                </div>
              </div>
            </div>
            <button
              onClick={() => handleResolveAlert(alert.id)}
              className="ml-4 p-1 rounded-full hover:bg-white/50 dark:hover:bg-gray-900/50 transition-colors"
              title="Resolve alert"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

// Toast notification helper
export const showSystemAlert = (alert) => {
  const options = {
    position: "top-right",
    autoClose: alert.type === 'critical' ? false : 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  };

  const message = (
    <div>
      <div className="font-medium">{alert.component} - {alert.type.toUpperCase()}</div>
      <div className="text-sm">{alert.message}</div>
    </div>
  );

  switch (alert.type) {
    case 'error':
    case 'critical':
      toast.error(message, options);
      break;
    case 'warning':
      toast.warn(message, options);
      break;
    case 'info':
      toast.info(message, options);
      break;
    default:
      toast(message, options);
  }
};